import React from "react";
import { Link as RouterLink } from "react-router-dom";
import { Box, Chip, Link, Typography } from "@mui/material";
import HomeSection from "../home/HomeSection";
import SearchInput from "../SearchInput";
import PlayerHeadshot from "../PlayerHeadshot";
import TeamLogo from "../TeamLogo";
import FeaturedPlayerTiles from "./FeaturedPlayerTiles";
import TeamLogoRail from "./TeamLogoRail";
import { DIRECTORY_GROUPS } from "./playerSearchCatalog";
import { colors } from "../../theme";
import { BRAND_COLORS, brandRgba } from "../../utils/brandAssets";
import { compareChipSx } from "../compare/compareChromeStyles";

const getDirectoryOptionLabel = (option) => {
  if (!option) return "";
  if (typeof option === "string") return option;
  return option.label || "";
};

const isDirectoryOptionEqual = (option, value) =>
  option?.type === value?.type && option?.id === value?.id;

const DirectoryOption = ({ option, sport, liProps }) => {
  const { key, ...otherProps } = liProps;
  return (
    <Box
      component="li"
      key={`${option.type}:${option.id}:${key}`}
      {...otherProps}
      sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
    >
      {option.type === "team" ? (
        <TeamLogo
          sport={sport}
          teamName={option.label}
          franchiseId={option.id}
          size={28}
        />
      ) : (
        <PlayerHeadshot
          playerId={option.id}
          sport={sport}
          size={28}
          showTooltip={false}
          showLoading={false}
        />
      )}
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" fontWeight={700} noWrap>
          {option.label}
        </Typography>
        {option.secondary ? (
          <Typography variant="caption" color="text.secondary" noWrap display="block">
            {option.secondary}
          </Typography>
        ) : null}
      </Box>
    </Box>
  );
};

const PlayerSearchView = ({
  catalog,
  searchQuery,
  onSearchInputChange,
  onSelectOption,
  featuredTiles = [],
  featuredLoading = false,
  featuredError = false,
  railTeams = [],
  railLoading = false,
  railError = false,
  onFeaturedSelect,
  onTeamSelect,
  onShortcut,
}) => {
  const teamsShortcut = catalog.shortcuts?.find((item) => item.id === "teams");
  const showFeatured =
    Boolean(catalog.featured) &&
    !featuredError &&
    (featuredLoading || featuredTiles.length > 0);
  const showTeams =
    Boolean(catalog.teamsRail) &&
    !railError &&
    (railLoading || railTeams.length > 0);

  return (
    <Box sx={{ py: { xs: 1, sm: 2 }, maxWidth: 1200, mx: "auto" }}>
      <Box
        sx={{
          mb: 3,
          p: { xs: 2, sm: 3 },
          borderRadius: 2,
          border: `1px solid ${colors.border}`,
          background: `linear-gradient(135deg, ${colors.paper} 0%, ${brandRgba(
            BRAND_COLORS.cyan,
            0.08
          )} 100%)`,
        }}
      >
        <Typography
          variant="overline"
          color="text.secondary"
          sx={{ letterSpacing: 1.2 }}
        >
          {catalog.overline}
        </Typography>
        <Typography variant="h4" component="h1" fontWeight={700} sx={{ mt: 0.5, mb: 1 }}>
          {catalog.title}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 560, mb: 2.5 }}>
          {catalog.subtitle}
        </Typography>
        <SearchInput
          value={null}
          onChange={(_, option) => {
            if (option && typeof option !== "string") onSelectOption?.(option);
          }}
          onInputChange={onSearchInputChange}
          searchQuery={searchQuery}
          getOptionLabel={getDirectoryOptionLabel}
          isOptionEqualToValue={isDirectoryOptionEqual}
          groupBy={(option) => option.group || DIRECTORY_GROUPS.players}
          renderOption={(props, option) => (
            <DirectoryOption
              option={option}
              sport={catalog.sport}
              liProps={props}
            />
          )}
          placeholder={catalog.placeholder}
          noOptionsText="No players or teams found"
        />
        {catalog.shortcuts?.length > 0 ? (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 2 }}>
            {catalog.shortcuts.map((shortcut) => (
              <Chip
                key={shortcut.id}
                label={shortcut.label}
                clickable
                variant="outlined"
                onClick={() => onShortcut?.(shortcut.path)}
                sx={compareChipSx()}
              />
            ))}
          </Box>
        ) : null}
      </Box>

      {showFeatured ? (
        <HomeSection
          title={catalog.featured.title}
          subtitle={catalog.featured.subtitle}
          noPadding
        >
          <FeaturedPlayerTiles
            players={featuredTiles}
            loading={featuredLoading}
            onSelect={onFeaturedSelect}
          />
        </HomeSection>
      ) : null}

      {showTeams ? (
        <HomeSection
          title={catalog.teamsRail.title}
          subtitle={catalog.teamsRail.subtitle}
          noPadding
          action={
            teamsShortcut ? (
              <Link
                component={RouterLink}
                to={teamsShortcut.path}
                underline="hover"
                color="text.secondary"
                variant="body2"
                fontWeight={600}
              >
                View all teams
              </Link>
            ) : null
          }
        >
          <TeamLogoRail
            teams={railTeams}
            loading={railLoading}
            onSelect={onTeamSelect}
          />
        </HomeSection>
      ) : null}
    </Box>
  );
};

export default PlayerSearchView;
